import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, ChevronDown, Zap, Package, Anchor, Building2, Settings, BarChart3, CheckCircle, Download } from 'lucide-react';
import { SEOHead } from '../components/SEOHead';
import { services, stats, industries, partners } from '../mockData';

const serviceIcons = [Zap, Package, Anchor, Building2, Settings, BarChart3];

export const HomePage = () => {
  return (
    <>
      <SEOHead
        title="Industrial Automation Company in UAE & Africa | Hexalogix Automation"
        description="Industrial automation company in UAE & Africa for PLC programming, SCADA development, BMS, HVAC automation, control panel manufacturing and switchgear support."
        keywords="industrial automation company UAE, industrial automation company Africa, PLC programming services UAE, SCADA development UAE, BMS solutions UAE, HVAC automation UAE, control panel manufacturing UAE, switchgear manufacturing UAE, automation solutions Africa"
      />

      <div className="min-h-screen">

        {/* ═══════════════════ HERO ═══════════════════ */}
        <section className="relative min-h-screen flex flex-col justify-center items-center overflow-hidden">
          {/* Background image */}
          <div className="absolute inset-0">
            <img
              src="/hero-desktop.webp"
              srcSet="/hero-mobile.webp 768w, /hero-desktop.webp 1600w"
              sizes="100vw"
              alt="Industrial automation facility"
              width="1600"
              height="900"
              fetchPriority="high"
              loading="eager"
              decoding="async"
              className="w-full h-full object-cover object-center"
            />
            {/* Strong dark overlay for text contrast */}
            <div className="absolute inset-0 bg-[#0A0F1E]/70" />
            {/* Bottom fade */}
            <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-[#0A0F1E] to-transparent" />
          </div>

          {/* Accent line at top */}
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[#0057B8] z-10" />

          {/* Hero Content — centered */}
          <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-36 sm:pt-32 pb-24">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-[0.9] tracking-tight mb-8">
              Industrial Automation Specialist
            </h1>

            <p className="text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed mb-10">
              Expert PLC Programming, BMS Solutions & Control Panel Manufacturing across UAE, Africa, and beyond.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center w-full sm:w-auto">
              <Link to="/contact" className="w-full sm:w-auto">
                <button className="inline-flex w-full sm:w-auto items-center justify-center gap-2 border-2 border-white/40 hover:border-white text-white font-semibold px-5 sm:px-8 py-4 text-sm sm:text-base tracking-wide transition-all duration-200 hover:bg-white/10 group">
                  Request a Technical Consultation
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </button>
              </Link>
              <a
                href="/Hexalogix-Brochure.pdf"
                download="Hexalogix-Automation-Brochure.pdf"
                className="inline-flex w-full sm:w-auto items-center justify-center gap-2 border-2 border-white/20 hover:border-[#0057B8] bg-white/5 hover:bg-[#0057B8]/20 text-white font-semibold px-5 sm:px-8 py-4 text-sm sm:text-base tracking-wide transition-all duration-200 group"
              >
                <Download className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
                Download Brochure
              </a>
            </div>
          </div>

          {/* Scroll indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-1 opacity-50">
            <span className="text-white text-[10px] tracking-widest uppercase">Scroll</span>
            <ChevronDown className="w-4 h-4 text-white animate-bounce" />
          </div>
        </section>

        {/* ═══════════════════ ABOUT SECTION ═══════════════════ */}
        <section className="bg-white py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto">
              {/* Animated label */}
              <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-6 opacity-0 animate-fadeIn" style={{animationDelay: '0.1s', animationFillMode: 'forwards'}}>
                <span className="w-8 sm:w-12 h-[3px] bg-gradient-to-r from-transparent to-[#0057B8] rounded-full" />
                <span className="text-[#0057B8] text-sm sm:text-lg font-bold tracking-widest uppercase text-center bg-gradient-to-r from-[#0057B8] to-[#003D82] bg-clip-text text-transparent">
                  About Hexalogix
                </span>
                <span className="w-8 sm:w-12 h-[3px] bg-gradient-to-l from-transparent to-[#0057B8] rounded-full" />
              </div>
              
              {/* Main heading with animation */}
              <h2 className="text-2xl sm:text-4xl md:text-5xl font-black text-[#0A0F1E] leading-tight mb-8 opacity-0 animate-fadeInUp" style={{animationDelay: '0.2s', animationFillMode: 'forwards'}}>
                Leading Industrial Automation Company in UAE, Africa & India
              </h2>
              
              {/* Paragraphs with animation */}
              <p className="text-gray-600 text-lg leading-relaxed mb-8 opacity-0 animate-fadeInUp" style={{animationDelay: '0.3s', animationFillMode: 'forwards'}}>
                Hexalogix Automation is an industrial automation company serving the UAE and Africa, specializing in PLC programming, SCADA system development, Building Management Systems (BMS), HVAC automation, control panel manufacturing, and low-voltage switchgear support.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed opacity-0 animate-fadeInUp" style={{animationDelay: '0.4s', animationFillMode: 'forwards'}}>
                We deliver end-to-end automation solutions for industries including HVAC, oil & gas, manufacturing, utilities, infrastructure, and commercial facilities, helping businesses improve efficiency, reduce downtime, and optimize operations across regional projects.
              </p>
            </div>
          </div>
          
          <style>{`
            @keyframes fadeIn {
              from {
                opacity: 0;
              }
              to {
                opacity: 1;
              }
            }
            
            @keyframes fadeInUp {
              from {
                opacity: 0;
                transform: translateY(20px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            
            .animate-fadeIn {
              animation: fadeIn 0.8s ease-out forwards;
            }
            
            .animate-fadeInUp {
              animation: fadeInUp 0.8s ease-out forwards;
            }
          `}</style>
        </section>

        {/* ═══════════════════ STATS STRIP ═══════════════════ */}
        <section className="bg-[#F0F4F9] border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-3 divide-x divide-gray-200">
              {stats.map((stat, index) => (
                <div key={index} className="py-8 px-6 md:px-8 text-center">
                  <div className="text-3xl md:text-4xl font-black text-[#0057B8] mb-1 tabular-nums">
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-500 uppercase tracking-widest font-medium">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#F0F4F9] py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Section header */}
            <div className="mb-14 text-center">
              <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-6 opacity-0 animate-fadeIn" style={{animationDelay: '0.1s', animationFillMode: 'forwards'}}>
                <span className="w-8 sm:w-12 h-[3px] bg-gradient-to-r from-transparent to-[#0057B8] rounded-full" />
                <span className="text-[#0057B8] text-sm sm:text-lg font-bold tracking-widest uppercase text-center bg-gradient-to-r from-[#0057B8] to-[#003D82] bg-clip-text text-transparent">
                  Our Services
                </span>
                <span className="w-8 sm:w-12 h-[3px] bg-gradient-to-l from-transparent to-[#0057B8] rounded-full" />
              </div>
              <div className="flex flex-col md:flex-row md:items-center md:justify-center gap-4">
                <h2 className="max-w-4xl mx-auto text-2xl sm:text-4xl md:text-5xl font-black text-[#0A0F1E] leading-tight opacity-0 animate-fadeInUp" style={{animationDelay: '0.2s', animationFillMode: 'forwards'}}>
                  Industrial Automation Solutions for UAE & Africa
                </h2>
              </div>
            </div>

            {/* Service cards grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.slice(0, 6).map((service, index) => {
                const Icon = serviceIcons[index] || Zap;
                return (
                  <Link key={service.id} to={`/services/${service.slug}`} className="group relative overflow-hidden aspect-[4/3] block">
                    {/* Background image */}
                    <img
                      src={service.image}
                      alt={`${service.title} for UAE and Africa industrial automation projects`}
                      width="800"
                      height="600"
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1E]/95 via-[#0A0F1E]/40 to-transparent" />
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-[#0057B8]/0 group-hover:bg-[#0057B8]/15 transition-colors duration-300" />

                    {/* Content */}
                    <div className="absolute inset-0 p-6 flex flex-col justify-end">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 bg-[#0057B8] flex items-center justify-center flex-shrink-0">
                          <Icon className="w-4 h-4 text-white" />
                        </div>
                      </div>
                      <h3 className="text-white font-bold text-lg leading-tight mb-2 group-hover:text-[#60A5FA] transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-gray-400 text-base leading-relaxed line-clamp-2 group-hover:text-gray-300 transition-colors">
                        {service.description}
                      </p>
                      {/* CTA arrow */}
                      <div className="flex items-center gap-1 mt-3 text-[#0057B8] text-sm font-semibold opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                        Learn More <ArrowRight className="w-3 h-3" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* ═══════════════════ INDUSTRIES ═══════════════════ */}
        <section className="bg-[#F0F4F9] py-20 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-14 text-center">
              <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-6 opacity-0 animate-fadeIn" style={{animationDelay: '0.1s', animationFillMode: 'forwards'}}>
                <span className="w-8 sm:w-12 h-[3px] bg-gradient-to-r from-transparent to-[#0057B8] rounded-full" />
                <span className="text-[#0057B8] text-sm sm:text-lg font-bold tracking-widest uppercase text-center bg-gradient-to-r from-[#0057B8] to-[#003D82] bg-clip-text text-transparent">
                  Our Reach
                </span>
                <span className="w-8 sm:w-12 h-[3px] bg-gradient-to-l from-transparent to-[#0057B8] rounded-full" />
              </div>
              <h2 className="text-2xl sm:text-4xl md:text-5xl font-black text-[#0A0F1E] leading-tight mb-8 opacity-0 animate-fadeInUp" style={{animationDelay: '0.2s', animationFillMode: 'forwards'}}>
                Industries We Serve
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-8 opacity-0 animate-fadeInUp" style={{animationDelay: '0.3s', animationFillMode: 'forwards'}}>
                Delivering precision automation solutions across diverse sectors worldwide.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {(industries || []).map((industry, i) => (
                <div
                  key={i}
                  className="group relative overflow-hidden cursor-default h-40"
                >
                  <img
                    src={industry.image || industry}
                    alt={industry.name || industry}
                    width="600"
                    height="400"
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  {/* Dark overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1E]/90 via-[#0A0F1E]/40 to-transparent group-hover:from-[#0057B8]/80 group-hover:via-[#0057B8]/30 transition-all duration-300" />
                  {/* Label */}
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <span className="text-white text-sm font-bold tracking-wide leading-tight block">
                      {industry.name || industry}
                    </span>
                  </div>
                  {/* Top accent line on hover */}
                  <div className="absolute top-0 left-0 right-0 h-[2px] bg-[#0057B8] scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════════ WHY CHOOSE US ═══════════════════ */}
        <section className="bg-[#F0F4F9] py-20 md:py-28 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              {/* Left: Image */}
              <div className="relative order-2 lg:order-1">
                <div className="relative opacity-0 animate-fadeInUp" style={{animationDelay: '0.1s', animationFillMode: 'forwards'}}>
                  <img
                    src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&h=700&fit=crop&q=85"
                    alt="Engineering excellence"
                    className="w-full h-[460px] object-cover shadow-2xl"
                  />
                  {/* Accent corner */}
                  <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-[#0057B8] -z-10" />
                  {/* Stats badge */}
                  <div className="absolute bottom-6 left-6 bg-[#0A0F1E] text-white p-5 shadow-xl">
                    <div className="text-3xl font-black text-[#0057B8]">500+</div>
                    <div className="text-sm text-gray-400 uppercase tracking-widest mt-1">Projects Delivered</div>
                  </div>
                </div>
              </div>

              {/* Right: Content */}
              <div className="order-1 lg:order-2">
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4 opacity-0 animate-fadeIn" style={{animationDelay: '0.2s', animationFillMode: 'forwards'}}>
                  <span className="w-8 sm:w-12 h-[3px] bg-gradient-to-r from-transparent to-[#0057B8] rounded-full" />
                  <span className="text-[#0057B8] text-sm sm:text-lg font-bold tracking-widest uppercase bg-gradient-to-r from-[#0057B8] to-[#003D82] bg-clip-text text-transparent">
                    Why Hexalogix
                  </span>
                  <span className="w-8 sm:w-12 h-[3px] bg-gradient-to-l from-transparent to-[#0057B8] rounded-full" />
                </div>
                <h2 className="text-3xl sm:text-4xl font-black text-[#0A0F1E] leading-tight mb-6 opacity-0 animate-fadeInUp" style={{animationDelay: '0.3s', animationFillMode: 'forwards'}}>
                  Engineering Excellence,<br />Delivered On-Site
                </h2>
                <p className="text-gray-600 leading-relaxed mb-8 opacity-0 animate-fadeInUp" style={{animationDelay: '0.4s', animationFillMode: 'forwards'}}>
                  Since 2019, Hexalogix Automation has been the trusted partner for industrial, marine, and energy sector clients across the UAE, Africa, and the Middle East — delivering turnkey automation solutions that perform.
                </p>

                <div className="space-y-4">
                  {[
                    { title: 'Expertise in Siemens, Allen Bradley, Schneider', desc: 'Certified engineers with extensive experience in leading PLC platforms' },
                    { title: 'UAE, Africa & India Project Experience', desc: 'Proven delivery experience across the UAE, African projects, and India-based engineering support' },
                    { title: 'End-to-End Automation Solutions', desc: 'Complete project execution from design to commissioning' },
                    { title: 'On-Site Commissioning & Support', desc: '24/7 technical assistance and rapid response support' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4 group">
                      <div className="flex-shrink-0 w-9 h-9 bg-[#0057B8]/10 border border-[#0057B8]/20 flex items-center justify-center mt-0.5 group-hover:bg-[#0057B8] transition-colors">
                        <CheckCircle className="w-4 h-4 text-[#0057B8] group-hover:text-white transition-colors" />
                      </div>
                      <div>
                        <h4 className="font-bold text-[#0A0F1E] text-base">{item.title}</h4>
                        <p className="text-gray-500 text-base">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-10">
                  <Link to="/about">
                    <button className="inline-flex items-center gap-2 bg-[#0A0F1E] hover:bg-[#0057B8] text-white font-semibold px-7 py-3.5 text-base tracking-wide transition-all duration-200 group">
                      Learn About Us
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════ FAQ SECTION ═══════════════════ */}
        <section className="bg-white py-20 md:py-28">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4 justify-center opacity-0 animate-fadeIn" style={{animationDelay: '0.1s', animationFillMode: 'forwards'}}>
                <span className="w-8 sm:w-12 h-[3px] bg-gradient-to-r from-transparent to-[#0057B8] rounded-full" />
                <span className="text-[#0057B8] text-sm sm:text-lg font-bold tracking-widest uppercase text-center bg-gradient-to-r from-[#0057B8] to-[#003D82] bg-clip-text text-transparent">
                  Frequently Asked Questions
                </span>
                <span className="w-8 sm:w-12 h-[3px] bg-gradient-to-r from-transparent to-[#0057B8] rounded-full" />
              </div>
              <h2 className="text-3xl sm:text-4xl font-black text-[#0A0F1E] leading-tight opacity-0 animate-fadeInUp" style={{animationDelay: '0.2s', animationFillMode: 'forwards'}}>
                Common Questions About Our Services
              </h2>
            </div>

            <div className="space-y-6">
              {[
                {
                  question: "What industries do you support?",
                  answer: "We support HVAC, oil & gas, manufacturing, utilities, infrastructure, commercial facilities, and Africa-based industrial automation projects."
                },
                {
                  question: "Do you provide PLC programming across UAE and Africa?",
                  answer: "Yes, we provide PLC programming services across the UAE and support industrial automation projects in Africa and regional markets."
                },
                {
                  question: "What BMS systems do you work with?",
                  answer: "We design and implement BMS and HVAC automation solutions for commercial, industrial, hospitality, and critical buildings in UAE and Africa."
                },
                {
                  question: "Do you provide control panel and switchgear manufacturing?",
                  answer: "Yes. We support control panel manufacturing, PLC panels, MCC panels, automation enclosures, and low-voltage switchgear requirements for UAE and Africa projects."
                }
              ].map((faq, i) => (
                <div key={i} className="border border-gray-200 bg-gray-50 p-6 hover:border-[#0057B8]/30 transition-colors">
                  <h3 className="font-bold text-[#0A0F1E] text-lg mb-3">{faq.question}</h3>
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════════ TECHNOLOGY PARTNERS ═══════════════════ */}
        <section className="bg-[#F0F4F9] py-16 border-y border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-center text-sm sm:text-lg font-bold tracking-widest uppercase mb-10">
              Trusted Brands We Use
            </p>
            <div className="flex flex-wrap justify-center items-center gap-6 md:gap-10">
              {partners.map((partner, i) => (
                <div
                  key={i}
                  className="group flex items-center justify-center h-16 px-6 bg-white border border-gray-200 hover:border-[#0057B8]/40 hover:shadow-md transition-all duration-300 cursor-default min-w-[130px]"
                  title={partner.name}
                >
                  {partner.logo ? (
                    <img
                      src={partner.logo}
                      alt={partner.name}
                      width="160"
                      height="64"
                      loading="lazy"
                      decoding="async"
                      className="h-8 w-auto object-contain grayscale group-hover:grayscale-0 transition-all duration-300"
                    />
                  ) : (
                    <span className="text-sm font-black text-gray-400 group-hover:text-[#0057B8] tracking-widest uppercase transition-colors">
                      {partner.name}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>



        {/* ═══════════════════ CTA BANNER ═══════════════════ */}
        <section className="relative bg-[#0057B8] py-20 md:py-24 overflow-hidden">
          {/* Geometric background accent */}
          <div className="absolute right-0 top-0 bottom-0 w-1/3 bg-[#003D82]/40 -skew-x-12 translate-x-16" />
          <div className="absolute right-16 top-0 bottom-0 w-16 bg-white/5 -skew-x-12" />

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-10">
            <div>
              <h2 className="text-2xl sm:text-4xl md:text-5xl font-black text-white leading-tight mb-4">
                Looking for a Reliable Industrial Automation Company in UAE & Africa?
              </h2>
              {/* <p className="text-blue-200 text-lg max-w-lg">
                Contact Hexalogix Automation today for expert PLC programming, SCADA systems, and BMS solutions.
              </p> */}
            </div>
            <div className="flex flex-col sm:flex-row gap-4 flex-shrink-0 w-full sm:w-auto">
              <Link to="/contact" className="w-full sm:w-auto">
                <button className="inline-flex w-full sm:w-auto items-center justify-center gap-2 bg-white text-[#0057B8] hover:bg-gray-100 font-bold px-5 sm:px-8 py-4 text-sm sm:text-base tracking-wide transition-all duration-200 hover:shadow-xl group">
                  Request a Project Consultation
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </Link>
            </div>
          </div>
        </section>

      </div>
    </>
  );
};

